import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { X, Plus, Trash2 } from 'lucide-react';

const CATEGORIES = ['Equality Act 2010', 'Disability Confident Scheme', 'Accessibility Support'];

export default function QuestionForm({ question, onClose, onSaved }) {
  const [form, setForm] = useState({
    question_text: question?.question_text || '',
    category: question?.category || 'Equality Act 2010',
    explanation: question?.explanation || '',
    options: question?.options?.length ? question.options : ['', '', '', ''],
    correct_answer: question?.correct_answer || '',
    is_active: question?.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const firstRef = useRef(null);

  useEffect(() => {
    firstRef.current?.focus();
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  function updateOption(i, val) {
    setForm(prev => {
      const options = [...prev.options];
      options[i] = val;
      return { ...prev, options };
    });
  }

  function addOption() {
    setForm(prev => ({ ...prev, options: [...prev.options, ''] }));
  }

  function removeOption(i) {
    setForm(prev => {
      const options = prev.options.filter((_, idx) => idx !== i);
      let correct = prev.correct_answer;
      if (correct === prev.options[i]) correct = '';
      return { ...prev, options, correct_answer: correct };
    });
  }

  async function submit(e) {
    e.preventDefault();
    const validOptions = form.options.filter(o => o.trim());
    if (validOptions.length < 2) { setError('Please provide at least 2 answer options'); return; }
    if (!form.correct_answer) { setError('Please select the correct answer'); return; }

    setSaving(true);
    setError(null);
    try {
      const data = {
        question_text: form.question_text,
        category: form.category,
        explanation: form.explanation,
        options: validOptions,
        correct_answer: form.correct_answer,
        is_active: form.is_active,
      };
      if (question) {
        await base44.entities.SLTQuestion.update(question.id, data);
      } else {
        await base44.entities.SLTQuestion.create(data);
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby="question-form-title" className="bg-background border border-border rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 id="question-form-title" className="text-base font-semibold text-foreground">{question ? 'Edit Question' : 'Add Question'}</h2>
          <button onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="q-text" className="block text-sm font-medium text-foreground mb-1">Question <span aria-hidden="true">*</span></label>
            <textarea ref={firstRef} id="q-text" required rows={2} value={form.question_text} onChange={e => setForm(p => ({ ...p, question_text: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring resize-none" />
          </div>
          <div>
            <label htmlFor="q-category" className="block text-sm font-medium text-foreground mb-1">Category <span aria-hidden="true">*</span></label>
            <select id="q-category" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-foreground">Answer Options <span aria-hidden="true">*</span></label>
              <span className="text-xs text-muted-foreground">Select the correct one</span>
            </div>
            <div className="space-y-2">
              {form.options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="correct"
                    checked={form.correct_answer === opt && opt.trim() !== ''}
                    onChange={() => setForm(p => ({ ...p, correct_answer: opt }))}
                    aria-label={`Mark option ${i + 1} as correct`}
                    className="w-4 h-4 accent-teal-600 shrink-0"
                    disabled={!opt.trim()}
                  />
                  <input
                    value={opt}
                    onChange={e => updateOption(i, e.target.value)}
                    placeholder={`Option ${String.fromCharCode(65 + i)}`}
                    className="flex-1 px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                  />
                  {form.options.length > 2 && (
                    <button type="button" onClick={() => removeOption(i)} aria-label={`Remove option ${i + 1}`} className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addOption} className="flex items-center gap-1.5 mt-2 text-xs text-teal-600 hover:text-teal-700 font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring rounded">
              <Plus className="w-3.5 h-3.5" /> Add option
            </button>
          </div>
          <div>
            <label htmlFor="q-explanation" className="block text-sm font-medium text-foreground mb-1">Explanation (optional)</label>
            <input id="q-explanation" value={form.explanation} onChange={e => setForm(p => ({ ...p, explanation: e.target.value }))} placeholder="Context shown after answering" className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring" />
          </div>
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-5 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400">
              {saving ? 'Saving…' : question ? 'Save Changes' : 'Add Question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}